import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'

interface User {
    id: number
    username: string
    avatar_url?: string
}

export const useAuthStore = defineStore('auth', () => {
    const token = ref<string | null>(localStorage.getItem('auth_token'))
    const user = ref<User | null>(null)
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    const isAuthenticated = computed(() => !!token.value && !!user.value)

    const login = async (username: string, password: string) => {
        isLoading.value = true
        error.value = null

        try {
            const response = await apiClient.post('/auth/login', {
                username,
                password
            })

            token.value = response.data.token
            user.value = response.data.user

            localStorage.setItem('auth_token', response.data.token)
            
            return true
        } catch (err: any) {
            error.value = err.response?.data?.error || 'Login failed'
            return false
        } finally {
            isLoading.value = false
        }
    }

    const logout = async () => {
        isLoading.value = true

        try {
            if (token.value) {
                await apiClient.post('/auth/logout')
            }
        } catch (err) {
            console.warn('Logout request failed:', err)
        } finally {
            token.value = null
            user.value = null
            localStorage.removeItem('auth_token')
            isLoading.value = false
        }
    }

    const verifyToken = async () => {
        if (!token.value) return false

        try {
            const response = await apiClient.get('/auth/verify')
            user.value = response.data.user
            return true
        } catch (err) {
            token.value = null
            user.value = null
            localStorage.removeItem('auth_token')
            return false
        }
    }

    const changePassword = async (currentPassword: string, newPassword: string) => {
        isLoading.value = true
        error.value = null

        try {
            await apiClient.post('/auth/change-password', {
                currentPassword,
                newPassword
            })
            return true
        } catch (err: any) {
            error.value = err.response?.data?.error || 'Password change failed'
            return false
        } finally {
            isLoading.value = false
        }
    }

    const updateProfile = async () => {
        try {
            const response = await apiClient.get('/users/me')
            user.value = response.data
        } catch (err) {
            console.error('Failed to update profile:', err)
        }
    }

    const uploadAvatar = async (file: File) => {
        isLoading.value = true
        error.value = null

        try {
            const formData = new FormData()
            formData.append('avatar', file)

            const response = await apiClient.post('/users/me/avatar', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            if (user.value) {
                user.value.avatar_url = response.data.avatar_url
            }

            return true
        } catch (err: any) {
            error.value = err.response?.data?.error || 'Avatar upload failed'
            return false
        } finally {
            isLoading.value = false
        }
    }

    const removeAvatar = async () => {
        isLoading.value = true
        error.value = null

        try {
            await apiClient.delete('/users/me/avatar')

            if (user.value) {
                user.value.avatar_url = undefined
            }

            return true
        } catch (err: any) {
            error.value = err.response?.data?.error || 'Avatar removal failed'
            return false
        } finally {
            isLoading.value = false
        }
    }

    const clearError = () => {
        error.value = null
    }

    return {
        token,
        user,
        isLoading,
        error,
        isAuthenticated,
        login,
        logout,
        verifyToken,
        changePassword,
        updateProfile,
        uploadAvatar,
        removeAvatar,
        clearError
    }
})